import { FC, KeyboardEvent, ReactNode, RefCallback, UIEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimationResult, CSS, animated, useTransition } from "react-spring";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { DesignTokenProps, createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Subheading } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { setDocumentOverflow } from "../../utilities";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { AlertProvider } from "../Alert";
import { Icon } from "../Icon";
import { OverlayContainer } from "../Overlay";
import { Tooltip, TooltipContainer } from "../Tooltip";
import { ApproveModal } from "./ApproveModal";
import { ConfirmModal } from "./ConfirmModal";
import { ModalCloseEventHandler } from "./ModalCloseEventHandler";

import tokenStyles from "./styles.module.css";

const useResolvedModalStyles = createComponentStyleHook<ModalProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "modal",
});

/**
 * The `Modal` component properties.
 */
export interface ModalProps extends BaseProps, DesignTokenProps {
  /**
   * Optional id for the modal element. If not provided one will be generated.
   */
  readonly id?: string;
  /**
   * The modal title.
   */
  readonly title?: ReactNode;

  /**
   * The modal content.
   */
  readonly children?: ReactNode;

  /**
   * The modal footer content.
   */
  readonly footer?: ReactNode;

  /**
   * Indicates whether the modal is visible.
   */
  readonly visible?: boolean;

  /**
   * The size of the modal. If not specified defaults to `"narrow"`.
   */
  readonly size?: "narrow" | "wide";

  /**
   * The optional root template for the modal.
   */
  readonly root?: (children?: ReactNode) => ReactNode;

  /**
   * Called when the close button is clicked.
   */
  readonly onClose?: ModalCloseEventHandler;
}

/**
 * The `Modal` component sub components.
 */
export interface ModalSubComponents {
  /**
   * Represents a confirmation modal with a single Confirm button.
   */
  readonly Confirm: typeof ConfirmModal;

  /**
   * Represents a confirmation modal with an Approve and Deny button.
   */
  readonly Approve: typeof ApproveModal;
}

/**
 * A component which displays a Modal dialog box.
 */
export const Modal = withSubComponents<FC<ModalProps>, ModalSubComponents>(
  {
    Confirm: ConfirmModal,
    Approve: ApproveModal,
  },
  (props) => {
    const { id, title, visible = false, children, footer, size = "narrow", root, onClose, testId } = props;
    const { mode, styles, StyleModeBoundary } = useResolvedModalStyles(props);
    const [enterState, setEnterState] = useState<"entering" | "exiting" | null>(null);
    const [isShadowTopVisible, setIsShadowTopVisible] = useState(false);
    const [isShadowBottomVisible, setIsShadowBottomVisible] = useState(false);
    const [showContent, setShowContent] = useState(false);

    const clientRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    const dialogId = useId();
    const titleId = useId();
    const contentId = useId();
    useEffect(() => {
      const clientHeight = clientRef?.current?.clientHeight || 0;
      const scrollTop = clientRef?.current?.scrollTop || 0;
      const scrollHeight = clientRef?.current?.scrollHeight || 0;

      if (clientHeight < scrollHeight - scrollTop) {
        setIsShadowBottomVisible(true);
      }
      setDocumentOverflow(visible ? "hidden" : "");
      return () => setDocumentOverflow("");
    }, [visible]);

    const transitions = useTransition(visible ? [visible] : [], {
      from: { opacity: 0, translateY: "-1rem" },
      enter: { opacity: 1, translateY: "0" },
      leave: { opacity: 0 },
      config: {
        duration: 300,
      },
      onStart: (result: AnimationResult) => {
        const opacity = (result.value as CSS.Properties).opacity;
        if (typeof opacity !== "number") return;

        const startOpacity = Math.round(opacity);
        if (startOpacity === 0) {
          setEnterState("entering");
        } else {
          setEnterState("exiting");
        }
      },
    });

    const baseHtmlProps = getHtmlPropsFromBaseProps(
      {
        ...props,
        testId: "modal",
      },
      {
        className: [styles.Modal, "modal", size, enterState],
      },
    );

    const refHandler = useCallback((ref: HTMLDialogElement | null) => {
      if (!ref) {
        setShowContent(false);
        return;
      }

      if (typeof ref.showModal === "function") {
        ref.showModal();
      } else {
        console.warn("HTMLDialogElement.showModal() is not supported in this environment.");
      }

      /*
        We have to do this because the <dialog> element automatically focuses the first focusable element unless another has 'autofocus=true' on it.
        however, React never adds this attribute if you set autoFocus={true} so the first element is always the close button (if available) which
        causes autoFocus elements to immediately lose focus. So what we do is as soon as the modal is added to the DOM and needs to be shown, we
        tick a frame and only then bring in the content, ensuring React forces focus away from the close button and to the relevant element.
      */
      requestAnimationFrame(() => {
        setShowContent(true);
      });

      // tick a frame and then show the content...
    }, []);

    const handleScroll = useCallback((ev: UIEvent<HTMLDivElement>) => {
      const { currentTarget } = ev;

      const clientHeight = currentTarget.clientHeight || 0;
      const scrollTop = currentTarget.scrollTop || 0;
      const scrollHeight = currentTarget.scrollHeight || 0;

      if (clientHeight + scrollTop + 1 < scrollHeight) {
        setIsShadowBottomVisible(true);
      } else {
        setIsShadowBottomVisible(false);
      }

      if (scrollTop > 0) {
        setIsShadowTopVisible(true);
      } else {
        setIsShadowTopVisible(false);
      }
    }, []);

    const keyDownHandler = useCallback(
      (ev: KeyboardEvent<HTMLDialogElement>) => {
        if (ev.code === "Escape") {
          ev.preventDefault();
          ev.stopPropagation();
          onClose?.();
        }
      },
      [onClose],
    );

    const content = (
      <Stack className={styles.ContentRoot} spacing="medium-large" testId={testId}>
        <div className="header-container">
          <Shelf className="header" alignment="top" spacing="medium-large">
            <Subheading id={title ? titleId : undefined}>{title}</Subheading>
            <Shelf.Spacer />
            {!!onClose && (
              <Tooltip content="Close">
                {(ref: RefCallback<HTMLElement>) => (
                  <button ref={ref} type="button" className={styles.CloseButton} onClick={onClose} aria-label="Close modal">
                    <Icon name="x" />
                  </button>
                )}
              </Tooltip>
            )}
          </Shelf>
          <div className={classNames("shadow", isShadowTopVisible ? "visible" : "hidden")}></div>
        </div>
        {showContent && (
          <Stack className="content-container" spacing="medium-large" reverse>
            <div className="footer-container">
              <div className={classNames("shadow", isShadowBottomVisible ? "visible" : "hidden")}></div>
              {!!footer && <div className="footer">{footer}</div>}
            </div>
            {!!children && (
              <div id={contentId} className="content" onScroll={handleScroll} ref={clientRef}>
                {children}
              </div>
            )}
          </Stack>
        )}
      </Stack>
    );

    useEffect(() => {
      /* Captures Escape key presses to dismiss the modal while preserving focus and input behavior
         inside editable elements. */
      if (!visible) return;
      const handleKey = (ev: globalThis.KeyboardEvent) => {
        // native DOM keydown handler (not React.KeyboardEvent)
        if (ev.key !== "Escape" && ev.code !== "Escape") return;

        const target = ev.target as HTMLElement | null;
        const d = dialogRef.current;

        // If focus is inside an input/textarea/contenteditable inside the modal, don't close.
        if (d && target && d.contains(target)) {
          const tag = target.tagName?.toLowerCase();
          const isEditable = tag === "input" || tag === "textarea" || target.getAttribute?.("contenteditable") === "true";
          if (isEditable) return;
        }

        ev.preventDefault();
        ev.stopPropagation();
        onClose?.();
      };

      document.addEventListener("keydown", handleKey, true);
      return () => document.removeEventListener("keydown", handleKey, true);
    }, [visible, onClose]);

    return (
      <StyleModeBoundary mode={mode}>
        {transitions((style) => (
          <animated.dialog
            {...baseHtmlProps}
            id={id ?? dialogId}
            ref={refHandler}
            style={{ ...baseHtmlProps.style, ...style }}
            onKeyDown={keyDownHandler}
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={children ? contentId : undefined}
            aria-modal
          >
            <AlertProvider>
              <OverlayContainer>
                <TooltipContainer>{root ? root(content) : content}</TooltipContainer>
              </OverlayContainer>
            </AlertProvider>
          </animated.dialog>
        ))}
      </StyleModeBoundary>
    );
  },
);
